import { filter, map, take, tap, withLatestFrom } from 'rxjs/operators'
import { authActions } from 'src/app/core/auth/auth.actions'
import { authSelectors } from 'src/app/core/auth/auth.selectors'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { LoggingService } from '../../shared/services/logging.service'
import { selectDevice } from './device.selectors'
import moment from 'moment-mini'
import { ConfigurationService } from '../../shared/services/configuration.service'
import { settingSelectors } from '../../core/settings/settings.selectors'
import { deviceActions } from './device.actions'
import { settingsActions } from '../../core/settings/settings.actions'
import { Router } from '@angular/router'
import { AppRoutesNS } from '../../app-routes.namespace'
import { userRoleSelectors } from '../role/user-role/user-role.selectors'
import { RolesType } from '../role/role'
import { environment } from '../../../environments/environment'
import { user } from '@angular/fire/auth'

@Injectable()
export class DeviceEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private notificationService: NotificationService,
    private loggingService: LoggingService,
    private configurationService: ConfigurationService,
    private router: Router
  ) {}

  validateDeviceLock = createEffect(() =>
    this.actions$.pipe(
      ofType(deviceActions.validateDeviceLock),
      withLatestFrom(
        this.store.select(settingSelectors.selectById, 'deviceId')
      ),
      map(([validateDeviceLock, deviceId]) => {
        const userRoles = validateDeviceLock.member.userRoles
        const isAdmin = userRoles?.filter(
          (userRole) => RolesType.ADMIN === userRole.role?.type
        ).length

        let deviceUuid: string = deviceId
        if (
          !deviceUuid ||
          isAdmin ||
          environment.moduleConfigs.authModule.ignoreDeviceLock
        ) {
          return deviceActions.validateDeviceLockSuccess()
        }

        this.store.dispatch(deviceActions.setDevice({ deviceId: deviceUuid }))

        if (!validateDeviceLock.member.lockedToDevice) {
          this.store.dispatch(
            authActions.updateProfile({
              params: {
                lockedToDevice: deviceUuid,
                lockedToDeviceUntil: moment().add(14, 'day')
              }
            })
          )
          return deviceActions.validateDeviceLockSuccess()
        }

        if (validateDeviceLock.member.lockedToDevice === deviceUuid) {
          return deviceActions.validateDeviceLockSuccess()
        }

        if (validateDeviceLock.member.lockedToDevice !== deviceUuid) {
          if (
            moment(
              validateDeviceLock.member.lockedToDeviceUntil
            ).isSameOrBefore(moment())
          ) {
            this.store.dispatch(
              authActions.updateProfile({
                params: {
                  lockedToDevice: deviceUuid,
                  lockedToDeviceUntil: moment().add(14, 'day')
                }
              })
            )

            return deviceActions.validateDeviceLockSuccess()
          }
        }

        return deviceActions.validateDeviceLockFailed()
      })
    )
  )

  validateDeviceLockFailed = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deviceActions.validateDeviceLockFailed),
        tap(() =>
          this.router.navigateByUrl(AppRoutesNS.Members.PROFILE_MYPROFILE)
        )
      ),
    { dispatch: false }
  )

  requestLockRelease = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deviceActions.requestLockRelease),
        withLatestFrom(this.store.select(authSelectors.selectMember)),
        tap(async () => {
          this.notificationService.emit(
            'deviceModule.requestingDeviceLockRelease',
            'warning'
          )

          await new Promise((resolve) => setTimeout(resolve, 4900))

          this.notificationService.emit(
            'deviceModule.registeringDeviceLockRelease',
            'warning'
          )

          await new Promise((resolve) => setTimeout(resolve, 4900))

          this.notificationService.emit(
            'deviceModule.deviceLockReleaseRequestSend',
            'warning'
          )

          this.store.dispatch(
            authActions.updateProfile({
              params: {
                lockedToDevice: null,
                lockedToDeviceUntil: null
              }
            })
          )

          this.store.dispatch(authActions.fetchMember())

          this.store
            .select(selectDevice)
            .pipe(take(1))
            .subscribe((deviceId) => {
              const uuid = deviceId ?? 'no valid uuid'

              this.loggingService.reportError(
                new Error(
                  `Device lock release request made from device uuid ${uuid}`
                )
              )
            })
        })
      ),
    { dispatch: false }
  )

  initDevice = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.loadMember, settingsActions.setMultipleSettings),
      withLatestFrom(
        this.configurationService.isMobileApp$,
        this.store.select(authSelectors.selectMember)
      ),
      filter(
        ([_, isMobileApp, selectMember]) => !!isMobileApp && !!selectMember
      ),
      map(([_loadMember, _isMobileApp, selectMember]) =>
        deviceActions.validateDeviceLock({ member: selectMember })
      )
    )
  )
}
